import { BasicCard } from '@common/CardWithTitle/BasicCard';
import { BasicCardContent } from '@common/CardWithTitle/BasicCardContent';
import {
  KeyboardArrowDown,
  SyncAltOutlined,
  TrendingUp,
} from '@mui/icons-material';
import {
  Box,
  styled,
  Typography,
  useTheme,
  Menu,
  IconButton,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { useAuditLogsStore } from '@stores/auditLogsStore';
import { useLocationsStore } from '@stores/locationsStore';
import { useProductsStore } from '@stores/productsStore';
import { useUsersStore } from '@stores/usersStore';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from 'recharts';
import { LineChart, Line, YAxis } from 'recharts';
import { AuditLogsClient } from 'src/Clients/AuditLogsClient';
import type { AuditLog } from 'src/Clients/AuditLogsClient';
import { format, parseISO } from 'date-fns';
import { useAuth } from '@Auth/useAuth';
import { AgGridReact } from 'ag-grid-react';
import { themeAlpine } from 'ag-grid-community';
import type { ColDef } from 'ag-grid-community';
import { AgGridTableContainer } from '@common/AgGridTableContainer';

interface MonthlyCount {
  month?: string;
  date?: string;
  count: number;
}

interface StockMovement {
  month?: string;
  date?: string;
  increase: number;
  decrease: number;
}

export const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [monthlyData, setMonthlyData] = useState<MonthlyCount[]>([]);
  const [stockMovements, setStockMovements] = useState<StockMovement[]>([]);
  const [monthsToShow, setMonthsToShow] = useState<number>(3);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { getAuditLogsByOrganizationId, auditLogs } = useAuditLogsStore();
  const { baseUser } = useAuth();
  const { getLocationsByOrganizationId, locations } = useLocationsStore();
  const { getProductsByOrganizationId, products } = useProductsStore();
  const { getUsersByOrganization, users } = useUsersStore();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (months: number) => {
    setMonthsToShow(months);
    handleClose();
  };

  useEffect(() => {
    if (baseUser?.organization_id) {
      getAuditLogsByOrganizationId(baseUser.organization_id);
      getLocationsByOrganizationId(baseUser.organization_id);
      getProductsByOrganizationId(baseUser.organization_id);
      getUsersByOrganization(baseUser.organization_id);
    }
  }, [
    baseUser?.organization_id,
    getAuditLogsByOrganizationId,
    getLocationsByOrganizationId,
    getProductsByOrganizationId,
    getUsersByOrganization,
  ]);

  useEffect(() => {
    const fetchMonthlyData = async () => {
      if (!baseUser?.organization_id) return;
      try {
        const [auditData, movementData] = await Promise.all([
          AuditLogsClient.getMonthlyAuditLogCountsByOrganizationId(
            baseUser.organization_id,
            monthsToShow
          ),
          AuditLogsClient.getMonthlyStockMovements(
            baseUser.organization_id,
            monthsToShow
          ),
        ]);
        setMonthlyData(auditData);
        setStockMovements(movementData);
      } catch (error) {
        console.error('Error fetching monthly data:', error);
      }
    };

    fetchMonthlyData();
  }, [baseUser?.organization_id, monthsToShow]);

  const formatDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) return '';
      try {
        if (monthsToShow < 1) {
          // For daily view (when dateStr is YYYY-MM-DD)
          return format(parseISO(dateStr), 'EEE d');
        }
        // For monthly view (when dateStr is YYYY-MM)
        return format(parseISO(dateStr + '-01'), 'MMM yyyy');
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateStr;
      }
    },
    [monthsToShow]
  );

  const chartData = useMemo(() => {
    return monthlyData.map((item) => ({
      name: formatDate(item.month || item.date || ''),
      value: item.count,
    }));
  }, [monthlyData, formatDate]);

  const stockChartData = useMemo(() => {
    return stockMovements.map((item) => ({
      name: formatDate(item.month || item.date || ''),
      increase: item.increase,
      decrease: item.decrease,
    }));
  }, [stockMovements, formatDate]);

  const getXAxisConfig = useMemo(() => {
    // Configure x-axis based on time range
    if (monthsToShow < 1) {
      // Daily view (7 days)
      return {
        angle: -45,
        textAnchor: 'end',
        interval: 0,
        height: 60,
      };
    } else if (monthsToShow <= 3) {
      // 3 months view
      return {
        angle: 0,
        textAnchor: 'middle',
        interval: 0,
        height: 30,
      };
    } else {
      // 6 months and 1 year view
      return {
        angle: -45,
        textAnchor: 'end',
        interval: monthsToShow > 6 ? 1 : 0, // Show every other label for 1 year
        height: 60,
      };
    }
  }, [monthsToShow]);

  type AuditLogRow = AuditLog & {
    date: string;
    formattedDate: string;
    delta?: number;
    productName?: string;
    locationName?: string;
    userName?: string;
  };

  const auditLogRows: AuditLogRow[] = useMemo(() => {
    const productMap: Map<string, string> = new Map(
      products.map((p) => [p.id, p.name] as [string, string])
    );
    const locationMap: Map<string, string> = new Map(
      locations.map((l) => [l.id, l.name] as [string, string])
    );
    const userMap: Map<string, string> = new Map(
      users.map((u) => [u.id, u.name] as [string, string])
    );

    return auditLogs.map((log) => {
      let formattedDate = '';
      try {
        formattedDate = format(new Date(log.created_at), 'MM/dd HH:mm');
      } catch {
        formattedDate = log.created_at;
      }

      let computedDelta: number | undefined = undefined;
      const isAddAction = /add|create|new/i.test(log.action || '');
      if (typeof log.quantity === 'number') {
        if (isAddAction) {
          computedDelta = log.quantity;
        } else if (typeof log.old_quantity === 'number') {
          computedDelta = log.quantity - log.old_quantity;
        } else {
          computedDelta = log.quantity;
        }
      }

      return {
        ...log,
        date: log.created_at,
        formattedDate,
        delta: computedDelta,
        productName: productMap.get(log.product_id),
        locationName: locationMap.get(log.location_id),
        userName: userMap.get(log.user_id),
      };
    });
  }, [auditLogs, products, locations, users]);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(undefined), []);

  const auditLogColumns: ColDef<AuditLogRow>[] = useMemo(() => {
    if (isMobile) {
      return [
        { field: 'formattedDate', headerName: 'Date', flex: 1, sortable: true },
        { field: 'productName', headerName: 'Item', flex: 1 },
        {
          field: 'delta',
          headerName: 'Δ Qty',
          flex: 0.6,
          valueFormatter: (p) =>
            p.value === undefined || p.value === null
              ? ''
              : `${p.value > 0 ? '+' : ''}${numberFormatter.format(p.value)}`,
          cellStyle: (p) => ({
            textAlign: 'right',
            color:
              typeof p.value === 'number'
                ? p.value > 0
                  ? theme.palette.success.main
                  : p.value < 0
                  ? theme.palette.error.main
                  : theme.palette.text.primary
                : theme.palette.text.primary,
          }),
        },
      ];
    }
    return [
      { field: 'formattedDate', headerName: 'Date', flex: 1, sortable: true },
      { field: 'action', headerName: 'Action', flex: 1, sortable: true },
      { field: 'entity_type', headerName: 'Entity', flex: 1 },
      { field: 'productName', headerName: 'Product', flex: 1 },
      { field: 'locationName', headerName: 'Location', flex: 1 },
      {
        field: 'old_quantity',
        headerName: 'Old Qty',
        flex: 0.7,
        valueFormatter: (p) =>
          p.value === undefined || p.value === null
            ? ''
            : numberFormatter.format(p.value as number),
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'quantity',
        headerName: 'New Qty',
        flex: 0.7,
        valueFormatter: (p) =>
          p.value === undefined || p.value === null
            ? ''
            : numberFormatter.format(p.value as number),
        cellStyle: { textAlign: 'right' },
      },
      {
        field: 'delta',
        headerName: 'Δ Qty',
        flex: 0.6,
        valueFormatter: (p) =>
          p.value === undefined || p.value === null
            ? ''
            : `${p.value > 0 ? '+' : ''}${numberFormatter.format(p.value)}`,
        cellStyle: (p) => ({
          textAlign: 'right',
          color:
            typeof p.value === 'number'
              ? p.value > 0
                ? theme.palette.success.main
                : p.value < 0
                ? theme.palette.error.main
                : theme.palette.text.primary
              : theme.palette.text.primary,
        }),
      },
      { field: 'userName', headerName: 'User', flex: 1 },
    ];
  }, [isMobile, numberFormatter, theme.palette.error.main, theme.palette.success.main, theme.palette.text.primary]);

  return (
    <>
    <DashboardContainer>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 2,
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          paddingBottom: '100px',
        }}
      >
        <Typography variant="h1">Dashboard</Typography>
        <Box>
          <SelectButton onClick={handleClick}>
            {monthsToShow < 1 ? `7 Days` : `${monthsToShow} Months`}{' '}
            <KeyboardArrowDown
              sx={{ fontSize: '14px', color: theme.palette.text.secondary }}
            />
          </SelectButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box
              sx={{
                flexDirection: 'column',
                width: '256px',
                padding: '4px',
              }}
            >
              <MenuItem onClick={() => handleMenuItemClick(7 / 30)}>
                7 Days
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(3)}>
                3 Months
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(6)}>
                6 Months
              </MenuItem>
              <MenuItem onClick={() => handleMenuItemClick(12)}>
                1 Year
              </MenuItem>
            </Box>
          </Menu>
        </Box>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
            <BasicCard>
              <BasicCardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': { cursor: 'pointer' },
                        width: '100%',
                      }}
                      onClick={() => {}}
                    >
                      <IconButton
                        sx={{
                          background: theme.palette.action.hover,
                          borderRadius: '4px',
                          fontSize: '14px',
                        }}
                      >
                        <SyncAltOutlined fontSize="inherit" />
                      </IconButton>
                      <Typography variant="h4">Audit Logs</Typography>
                    </Box>
                    <Typography variant="subtitle1">Changes</Typography>
                    <Typography variant="body1">{auditLogs.length}</Typography>
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={chartData}>
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                      {...getXAxisConfig}
                    />
                    <Tooltip cursor={{ fill: theme.palette.action.hover }} />
                    <Bar dataKey="value" fill={theme.palette.primary.main} />
                  </BarChart>
                </ResponsiveContainer>
              </BasicCardContent>
            </BasicCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
            <BasicCard>
              <BasicCardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    width: '100%',
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': { cursor: 'pointer' },
                        width: '100%',
                      }}
                      onClick={() => {}}
                    >
                      <IconButton
                        sx={{
                          background: theme.palette.action.hover,
                          borderRadius: '4px',
                          fontSize: '14px',
                        }}
                      >
                        <TrendingUp fontSize="inherit" />
                      </IconButton>
                      <Typography variant="h4">Stock Movement</Typography>
                    </Box>
                    <Typography variant="subtitle1">
                      Increases vs Decreases
                    </Typography>
                    <Typography variant="body1">
                      {stockMovements.reduce(
                        (acc, curr) => acc + curr.increase - curr.decrease,
                        0
                      )}
                    </Typography>
                  </Box>
                </Box>
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={stockChartData}>
                    <XAxis
                      dataKey="name"
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                      {...getXAxisConfig}
                    />
                    <YAxis
                      axisLine={{ stroke: theme.palette.action.hover }}
                      tickLine={false}
                      tick={{
                        fill: theme.palette.text.secondary,
                        fontSize: 12,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="increase"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.success.main }}
                    />
                    <Line
                      type="monotone"
                      dataKey="decrease"
                      stroke={theme.palette.error.main}
                      strokeWidth={2}
                      dot={{ fill: theme.palette.error.main }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </BasicCardContent>
            </BasicCard>
          </Grid>
        </Grid>
      </Box>
    {/* Recent logs and inventory changes table */}
    <Box sx={{ padding: 2, width: '100%', maxWidth: '1200px', margin: '0 auto', paddingBottom: '100px' }}>
      <Typography variant="h2" sx={{ mb: 1 }}>Recent Activity</Typography>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Latest audit logs and inventory changes
      </Typography>
      <AgGridTableContainer>
        <AgGridReact<AuditLogRow>
          theme={themeAlpine}
          rowData={auditLogRows}
          columnDefs={auditLogColumns}
          domLayout="autoHeight"
          rowHeight={38}
          headerHeight={42}
          pagination={true}
          paginationPageSize={10}
          paginationPageSizeSelector={[10, 20, 50]}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            flex: 1,
          }}
          getRowId={(params) => params.data.id}
        />
      </AgGridTableContainer>
    </Box>
    </DashboardContainer>

    </>
  );
};

const SelectButton = styled('button')(({ theme }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: '4px 10px',
  cursor: 'pointer',
  fontSize: '14px',
  height: '26px',
  gap: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MenuItem = styled('li')(({ theme }) => ({
  padding: '6px 8px',
  height: '28px',
  fontSize: '14px',
  fontWeight: 400,
  color: theme.palette.mode === 'light' ? '#525252' : '#d4d4d4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease-in-out',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
  },
}));

const DashboardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  width: '100%',
  height: '100vh', // Account for header/navigation
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '0px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.action.hover,
  },
}));
